import * as SelectPrimitive from '@radix-ui/react-select';
import { useControllableState } from '@radix-ui/react-use-controllable-state';
import { ComponentPropsWithoutRef, createContext, ElementRef, forwardRef, ReactNode, useContext } from 'react';
import { Check, KeyboardArrowDownRounded } from '@streamelements/frontend-icons';
import { Box, type CSSProp, styled, Text } from '@streamelements/frontend-ui';

const SelectContext = createContext({ hasValue: false });

const onFocus = '&[data-focused="true"]';
const withValue = '&[data-has-value="true"]';
const withValueIdle = '&[data-has-value="true"]:not([data-focused="true"])';

const withPlaceholder = '&[data-has-placeholder="true"]';
const withPlaceholderIdle = '&[data-has-placeholder="true"]:not([data-focused="true"])';

const FieldSet = styled('fieldset', {
  display: 'grid',
  gridTemplateColumns: '1fr',
  alignItems: 'center',
  justifyContent: 'flex-start',
  borderRadius: '0.5rem',
  fontSize: '$bodyText',
  fontFamily: '$base',
  color: '$textPrimaryMain',
  padding: '0.875rem 0.125rem 0.65rem',
  width: '100%',

  $$focusOutlineColor: '$colors$uiPrimary16A',
  $$focusOutlineSize: '2px',
  $$outlineColor: 'transparent',
  $$outlineWidth: '1px',

  $$labelIdle: '$colors$textPrimary30',
  $$labelFocus: '$colors$uiPrimaryMain',
  $$labelWithValueIdle: '$colors$uiPrimaryMain',

  $$borderIdle: '$colors$uiPrimary50',
  $$borderFocus: '$colors$uiPrimaryMain',

  $$labelScale: 1,
  $$labelTranslateX: '1rem',
  $$labelTranslateY: '0.6rem',
  $$labelColor: '$$labelIdle',
  $$borderColor: '$$borderIdle',

  position: 'relative',
  border: '$$outlineWidth solid $$outlineColor',

  '&:focus-visible': {
    $$outlineWidth: '$$focusOutlineSize',
    $$outlineColor: '$$focusOutlineColor',
  },

  [`${onFocus}, &.state--focused`]: {
    // $$borderColor: '$colors$uiPrimaryMain'
    $$borderColor: '$$borderFocus',
  },

  [`${onFocus}, &.state--focused, ${withValue}, ${withPlaceholder}`]: {
    $$legendMaxWidth: '9999px',
    $$labelColor: '$$labelFocus',
    $$labelScale: 0.85,
    $$labelTranslateY: '-0.5rem',
    $$labelTranslateX: '1rem',

    '&[data-label]': {
      $$legendPx: '$space$base',
    },
  },

  [`${withValueIdle}, ${withPlaceholderIdle}`]: {
    $$labelColor: '$$labelWithValueIdle',
  },

  '&[disabled]': {
    $$borderColor: '$colors$uiDisabledMain',
    $$labelColor: '$colors$uiDisabledMain',
    opacity: 0.3,
  },

  variants: {
    reset: {
      true: {
        margin: 0,
        appearance: 'none',
        background: 'none',
      },
    },

    outlined: {
      true: {
        $$focusOutlineSize: '1px',
        $$focusOutlineColor: '$colors$uiPrimaryMain',
        $$outlineColor: '$colors$uiPrimary50',

        justifyContent: 'space-between',
        pl: '0.5rem',
        pr: '2.5rem',
      },
    },
  },

  defaultVariants: {
    reset: true,
    outlined: true,
  },
});

const Label = styled(Text.Body, {
  position: 'absolute',
  left: 0,
  top: 0,
  transformOrigin: 'left',
  transform: 'translate($$labelTranslateX, $$labelTranslateY) scale($$labelScale)',
  WebkitTransform: 'translate($$labelTranslateX, $$labelTranslateY) scale($$labelScale)',
  transition: 'color 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms,transform 200ms cubic-bezier(0.0, 0, 0.2, 1) 0ms',
  pointerEvents: 'none',
  color: '$$labelColor',
  userSelect: 'none',
  maxWidth: 'calc(100% - 1rem - var(--startAdornmentSize) - var(--endAdornmentSize))',
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
  overflow: 'hidden',

  // [`${onFocusParent}, ${withValueParent}, ${withPlaceholderParent}, .state--focused &`]: {
  //   maxWidth: 'calc(100% - 0.5rem)',
  // },
});

const StyledContent = styled(SelectPrimitive.Content, {
  bgc: '$uiSecondary',
  br: '0.5rem',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  alignItems: 'center',
  gap: '0.25rem',
  boxShadow: '$2',

  variants: {
    isPopper: {
      true: {
        '&[data-side="bottom"]': {
          transform: 'translateY(1px)',
        },
        '&[data-side="left"]': {
          transform: 'translateX(-1px)',
        },
        '&[data-side="right"]': {
          transform: 'translateX(1px)',
        },
        '&[data-side="top"]': {
          transform: 'translateY(-1px)',
        },
      },
    },
  },
});

const StyledViewport = styled(SelectPrimitive.Viewport, {
  width: '100%',
  padding: '0.25rem',
  overflow: 'auto',

  variants: {
    isPopper: {
      true: {
        height: 'var(var(--radix-select-trigger-height))',
        overflow: 'auto',
        width: '100%',
        minWidth: 'var(--radix-select-trigger-width)',
      },
    },
  },
});

const StyledItem = styled(SelectPrimitive.Item, {
  outline: 'none',
  boxSizing: 'border-box',
  fontSize: '$bodyText',
  lineHeight: 1,
  color: '$textPrimaryMain',
  borderRadius: 4,
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  padding: '0.75rem 0.5rem',
  position: 'relative',
  userSelect: 'none',
  fontFamily: '$base',
  width: '100%',
  cursor: 'pointer',

  '&[data-disabled]': {
    color: '$textPrimary30',
    pointerEvents: 'none',
  },

  '&[data-highlighted]': {
    backgroundColor: '$uiPrimary16A',
    color: '$uiPrimaryMain',
  },

  '&[hidden]': {
    height: 0,
    padding: 0,
    overflow: 'hidden',
  },

  variants: {
    reset: {
      true: {
        appearance: 'none',
        background: 'none',
        border: 'none',
      },
    },
  },
  defaultVariants: {
    reset: true,
  },
});

const StyledLabel = styled(SelectPrimitive.Label, {
  padding: '0 25px',
  fontSize: 12,
  lineHeight: '25px',
  color: '$textPrimaryMain',
});

const StyledSeparator = styled(SelectPrimitive.Separator, {
  height: 1,
  backgroundColor: '$uiPrimary30',
  margin: 5,
});

const SelectTrigger = forwardRef<
  ElementRef<typeof SelectPrimitive.Trigger>,
  ComponentPropsWithoutRef<typeof SelectPrimitive.Trigger> & CSSProp & { label?: string }
>(({ children, label, ...props }, ref) => {
  const { hasValue } = useContext(SelectContext);
  return (
    <SelectPrimitive.Trigger ref={ref} asChild {...props} data-has-value={hasValue}>
      <FieldSet>
        {label && (
          <>
            <Box
              as="legend"
              css={{
                height: 0,
                visibility: 'hidden',
                opacity: 0,
                pl: '0.5rem',
                fontSize: '0.795rem',
                '[data-has-value="false"] &': { display: 'none' },
              }}
            >
              {label}
            </Box>
            <Label
            // css={{
            //   color: '$$focusOutlineColor',
            //   position: 'absolute',
            //   // bottom: '100%',
            //   transformOrigin: 'left center',
            //   transform: 'translateY($$labelTranslateY) translateX($$labelTranslateX) scale($$labelScale)',
            //   left: '1rem',
            // }}
            >
              {label}
            </Label>
          </>
        )}
        {children}
        <Box
          as={SelectPrimitive.Icon}
          css={{
            display: 'grid',
            placeItems: 'center',
            color: '$$labelColor',
            placeSelf: 'center',
            svg: {
              position: 'absolute',
              top: '50%',
              right: '0.5rem',
              transform: 'translateY(-50%)',
            },
          }}
        >
          <KeyboardArrowDownRounded />
        </Box>
      </FieldSet>
    </SelectPrimitive.Trigger>
  );
});
SelectTrigger.displayName = SelectPrimitive.Trigger.displayName;

const SelectContent = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Content> & CSSProp
>(({ children, position = 'item-aligned', side = 'bottom', sideOffset = 8, ...props }, ref) => (
  <SelectPrimitive.Portal>
    <StyledContent
      ref={ref}
      position={position}
      isPopper={position === 'popper'}
      side={side}
      sideOffset={sideOffset}
      onScroll={(e) => e.stopPropagation()}
      {...props}
    >
      <StyledViewport isPopper={position === 'popper'}>{children}</StyledViewport>
    </StyledContent>
  </SelectPrimitive.Portal>
));
SelectContent.displayName = SelectPrimitive.Content.displayName;

const SelectLabel = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Label>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Label>
>(({ ...props }, ref) => <StyledLabel {...props} ref={ref} />);
SelectLabel.displayName = SelectPrimitive.Label.displayName;

const SelectItem = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Item>
>(({ children, ...props }, ref) => (
  <StyledItem {...props} ref={ref}>
    <Box
      as="span"
      css={{
        // absolute right-2 flex h-3.5 w-3.5 items-center justify-center
        position: 'absolute',
        right: 8,
        display: 'flex',
        height: '0.875rem',
        width: '0.875rem',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <SelectPrimitive.ItemIndicator>
        <Box as={Check} css={{ '&&': { fontSize: '1rem' } }} />
      </SelectPrimitive.ItemIndicator>
    </Box>
    <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
  </StyledItem>
));
SelectItem.displayName = SelectPrimitive.Item.displayName;

const SelectSeparator = forwardRef<
  React.ElementRef<typeof SelectPrimitive.Separator>,
  React.ComponentPropsWithoutRef<typeof SelectPrimitive.Separator>
>((props, ref) => <StyledSeparator {...props} ref={ref} />);

SelectSeparator.displayName = SelectPrimitive.Separator.displayName;

const StyledSelect = styled(SelectPrimitive.Select, {});

function Select({ ...props }: React.ComponentPropsWithoutRef<typeof StyledSelect>) {
  return (
    <SelectContext.Provider value={{ hasValue: props.value !== undefined }}>
      <StyledSelect {...props} data-has-value={props.value !== undefined} />
    </SelectContext.Provider>
  );
}

Select.displayName = StyledSelect.displayName;

export const SelectGroup = SelectPrimitive.Group;
export const SelectValue = styled(SelectPrimitive.Value, {});

type SimpleSelectProps = {
  options: Map<string, ReactNode>;
  disabledOptions?: Set<string>;
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  name?: string;
  placeholder?: string;
  label?: string;
  disabled?: boolean;
  open?: boolean;
} & Pick<SelectPrimitive.SelectContentProps, 'position'> &
  CSSProp;

export function SelectSimple({
  options,
  disabledOptions = new Set(),
  value,
  defaultValue,
  onChange,
  name,
  label,
  placeholder,
  disabled,
  position,
  css,
  open,
}: SimpleSelectProps) {
  const [state, setState] = useControllableState({
    prop: value,
    defaultProp: defaultValue,
    onChange,
  });

  return (
    <Select {...{ name, value: state, onValueChange: setState, disabled, open }}>
      <SelectTrigger label={label} css={css}>
        <Text.Body
          css={{
            px: '0.375rem',
            mt: '-0.125rem',
            lineHeight: 1,
            minHeight: '1em',
            display: 'flex',
            alignItems: 'center',
            maxWidth: '100%',
            overflow: 'hidden',
            span: {
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              width: '100%',
            },
          }}
        >
          <SelectValue placeholder={placeholder} />
        </Text.Body>
      </SelectTrigger>
      <SelectContent position={position}>
        {Array.from(options).map(([value, label]) => (
          <SelectItem key={value} value={value} disabled={disabledOptions.has(value)}>
            {label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export { SelectTrigger, SelectContent, SelectLabel, SelectItem, SelectSeparator };
